import { logout } from "@/lib/auth-utils"
import { readApiErrorMessage } from "@/lib/read-api-error-message"
import { TokenRefresh } from "@/lib/token-refresh"
import { API_ROUTES } from "./api-routes"

export interface ApiError extends Error {
  status?: number
  data?: unknown
}

export type ApiRequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
  body?: object | FormData
  headers?: HeadersInit
  params?: Record<string, string | number | boolean | undefined>
  /** Override host (e.g. Strapi `getCmsApiBase()`). Defaults to Stream Hub API. */
  baseUrl?: string
}

const MAX_NETWORK_RETRIES = 3
const RETRY_BASE_DELAY_MS = 400
let isLoggingOut = false

function getBaseUrl(): string {
  return (
    process.env.NEXT_PUBLIC_API_URL ||
    "https://streamerhub-be-dev.streamstats.net"
  )
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

const createApiError = (response: Response, data: unknown): ApiError => {
  const error = new Error(
    readApiErrorMessage(data) || response.statusText || "API Error"
  ) as ApiError
  error.status = response.status
  error.data = data
  return error
}

const isNetworkError = (error: unknown): boolean =>
  error instanceof TypeError ||
  (error instanceof DOMException && error.name === "AbortError")

const shouldUseIdToken = (endpoint: string) => endpoint === API_ROUTES.authMe

const request = async <T>(
  endpoint: string,
  {
    method = "GET",
    body,
    headers: extraHeaders,
    params,
    baseUrl,
  }: ApiRequestOptions,
  retryOnUnauthorized: boolean
): Promise<T> => {
  const token = localStorage.getItem(
    shouldUseIdToken(endpoint) ? "id_token" : "access_token"
  )
  if (!token) {
    if (!isLoggingOut) {
      isLoggingOut = true
      logout()
    }
    throw new Error("No authenticated session.")
  }

  const isFormData = body instanceof FormData
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined) searchParams.set(key, String(value))
  }
  const headers: HeadersInit = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    Authorization: `Bearer ${token}`,
    ...extraHeaders,
  }
  const url = `${baseUrl ?? getBaseUrl()}${endpoint}${
    searchParams.size > 0 ? `?${searchParams.toString()}` : ""
  }`
  let requestBody: BodyInit | undefined
  if (method !== "GET" && body) {
    requestBody = isFormData ? body : JSON.stringify(body)
  }
  const response = await fetch(url, {
    method,
    headers,
    body: requestBody,
  })

  if (response.status === 401) {
    if (retryOnUnauthorized) {
      const refreshed = await TokenRefresh.refreshTokens()
      if (refreshed) {
        return request<T>(
          endpoint,
          { method, body, headers: extraHeaders, params, baseUrl },
          false
        )
      }
    }

    if (!isLoggingOut) {
      isLoggingOut = true
      logout()
    }
  }

  let responseData: unknown = null
  try {
    responseData = await response.json()
  } catch {
    // Some successful mutations return an empty response.
  }

  if (!response.ok) {
    if (
      response.status === 403 &&
      endpoint === API_ROUTES.authMe &&
      !isLoggingOut
    ) {
      isLoggingOut = true
      logout()
    }
    throw createApiError(response, responseData)
  }

  return responseData as T
}

export const apiClient = async <T>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> => {
  const method = options.method ?? "GET"
  let attempt = 0

  while (true) {
    try {
      return await request<T>(endpoint, { ...options, method }, true)
    } catch (error) {
      if (
        method !== "GET" ||
        !isNetworkError(error) ||
        attempt >= MAX_NETWORK_RETRIES
      ) {
        throw error
      }
      attempt += 1
      await sleep(RETRY_BASE_DELAY_MS * attempt)
    }
  }
}
